"use client";

import { useEffect, useRef, useState } from "react";

const testimonials = [
  {
    quote: "BlueSea Marine handled our sludge discharge at Nhava Sheva with absolute professionalism. Zero delays, full documentation — exactly what we need between port calls.",
    name: "Capt. Rajesh Nair",
    role: "Master, MV Pacific Horizon",
    company: "Transworld Shipping Ltd.",
    initial: "R",
    color: "#2196F3",
  },
  {
    quote: "We\'ve been using BlueSea for vessel stores and provisions across Mumbai and Kandla for three years. Their response time is unmatched and the quality is consistently reliable.",
    name: "Capt. Ahmad Al-Farsi",
    role: "Ship Master, MT Gulf Star",
    company: "Al Mansoori Marine LLC, UAE",
    initial: "A",
    color: "#C8A84B",
  },
  {
    quote: "The draught survey and cargo supervision at Haldia were conducted with precision. Their survey team\'s reports are thorough and accepted without question by our underwriters.",
    name: "Mr. Suresh Pillai",
    role: "Operations Manager",
    company: "Bharati Shipping Agencies, Kochi",
    initial: "S",
    color: "#4CAF50",
  },
  {
    quote: "Excellent coordination for bilge water removal at Colombo Port. BlueSea\'s team understood MARPOL requirements perfectly — no compliance issues whatsoever.",
    name: "Capt. Dilshan Perera",
    role: "Master, MV Ceylon Pride",
    company: "Lanka Maritime Services, Sri Lanka",
    initial: "D",
    color: "#FF5722",
  },
  {
    quote: "From portable fire extinguisher recharging to engine room stores — BlueSea is our go-to supplier at every Indian port. Their network saves us time and paperwork.",
    name: "Capt. Mohammed Hossain",
    role: "Chief Officer, MV Dhaka Star",
    company: "Bengal Shipping Corp., Chittagong",
    initial: "M",
    color: "#9C27B0",
  },
];

export default function TestimonialsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setActive((prev) => (prev + 1) % testimonials?.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const current = testimonials?.[active];

  return (
    <section id="testimonials" ref={sectionRef} className="py-28 md:py-36 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-navy-800/20" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-ocean-700/5 blur-[100px] pointer-events-none rounded-full" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          className={`text-center mb-16 ${visible ? "animate-fade-up" : "opacity-0"}`}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-8 h-px bg-gold-500" />
            <span className="text-[11px] uppercase tracking-[0.4em] text-gold-400 font-bold">Client Voices</span>
            <div className="w-8 h-px bg-gold-500" />
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
            <span className="text-white">From the</span>{" "}
            <span className="text-gradient-gold italic">Bridge & Deck</span>
          </h2>
          <p className="mt-4 text-[#8BA3BC] text-lg max-w-2xl mx-auto leading-relaxed">
            Trusted by Masters, Chief Officers, and Shipping Agents across 4 countries.
          </p>
        </div>

        {/* Main testimonial display */}
        <div
          className={`max-w-4xl mx-auto ${visible ? "animate-scale-in" : "opacity-0"}`}
          style={{ animationDelay: "0.2s" }}
        >
          <div className="glass-card rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
            {/* Decorative quote mark */}
            <div className="absolute top-8 left-10 text-[120px] font-display font-bold leading-none text-white/3 select-none pointer-events-none">
              "
            </div>

            {/* Glow */}
            <div
              className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-48 rounded-full blur-[80px] opacity-10 pointer-events-none transition-all duration-700"
              style={{ background: current?.color }}
            />

            {/* Quote */}
            <p className="text-xl md:text-2xl text-white leading-relaxed font-medium mb-10 relative z-10 transition-all duration-500">
              "{current?.quote}"
            </p>

            {/* Attribution */}
            <div className="flex flex-col items-center gap-3 relative z-10">
              <div
                className="w-12 h-0.5 mb-2 rounded-full"
                style={{ background: current?.color }}
              />
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-black text-white"
                style={{ background: `linear-gradient(135deg, ${current?.color}40, ${current?.color}20)`, border: `2px solid ${current?.color}40` }}
              >
                {current?.initial}
              </div>
              <div>
                <div className="text-white font-bold text-lg">{current?.name}</div>
                <div className="text-[#8BA3BC] text-[13px]">{current?.role}</div>
                <div className="text-[12px] font-semibold uppercase tracking-widest mt-1" style={{ color: current?.color }}>
                  {current?.company}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation dots */}
          <div className="flex items-center justify-center gap-3 mt-8">
            {testimonials?.map((_, i) => (
              <button
                key={i}
                onClick={() => { setActive(i); setIsAutoPlaying(false); }}
                className={`transition-all duration-500 rounded-full ${
                  i === active
                    ? "w-8 h-2 bg-ocean-400" :"w-2 h-2 bg-white/15 hover:bg-white/30"
                }`}
                aria-label={`View testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Thumbnail row */}
        <div
          className={`mt-12 flex flex-wrap justify-center gap-4 ${visible ? "animate-fade-up" : "opacity-0"}`}
          style={{ animationDelay: "0.4s" }}
        >
          {testimonials?.map((t, i) => (
            <button
              key={i}
              onClick={() => { setActive(i); setIsAutoPlaying(false); }}
              className={`px-4 py-2.5 rounded-full border transition-all duration-300 text-[12px] font-semibold ${
                i === active
                  ? "border-ocean-400/50 bg-ocean-400/10 text-white" :"border-white/10 text-[#8BA3BC] hover:border-white/20 hover:text-white"
              }`}
            >
              {t?.name?.split(" ")?.slice(0, 2)?.join(" ")}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}